import { createSignal, For, type JSX, Show } from "solid-js";
import type { DumpIconName } from "../generated/dump-icons";
import { DumpIcon } from "./DumpIcon";

export type DumpListEntry = {
  id: number | string;
  title: string;
  titleGrey?: string;
  description?: string;
  avatar?: string | null;
  icon?: DumpIconName;
  leftLabel?: string;
  rightLabel?: string;
  endLabel?: JSX.Element;
  danger?: boolean;
  raw?: unknown;
};

export type DumpAdvancedSearchField = {
  key: string;
  label: string;
  type?: "text" | "number" | "datetime-local" | "select";
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
};

export function dumpFilterQuery(
  filters: Record<string, string | number | undefined>,
): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") query[`filters[${key}]`] = value;
  }
  return query;
}

type DumpListProps = {
  label: string;
  entries: DumpListEntry[];
  loading?: boolean;
  actions?: JSX.Element;
  search?: string;
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  onAdd?: () => void;
  onAdvancedSearch?: () => void;
  advancedFields?: DumpAdvancedSearchField[];
  advancedValues?: Record<string, string | number | undefined>;
  onAdvancedApply?: (values: Record<string, string>) => void;
  onSelect?: (entry: DumpListEntry, controlKey: boolean) => void;
};

export function DumpList(props: DumpListProps) {
  const [advancedOpen, setAdvancedOpen] = createSignal(false);
  const [advancedDraft, setAdvancedDraft] = createSignal<Record<string, string>>({});
  const openAdvanced = () => {
    const values: Record<string, string> = {};
    for (const field of props.advancedFields ?? []) {
      const value = props.advancedValues?.[field.key];
      if (value !== undefined && value !== null) values[field.key] = String(value);
    }
    setAdvancedDraft(values);
    setAdvancedOpen(true);
    props.onAdvancedSearch?.();
  };
  const applyAdvanced = () => {
    props.onAdvancedApply?.(advancedDraft());
    setAdvancedOpen(false);
  };
  const resetAdvanced = () => {
    setAdvancedDraft({});
    props.onAdvancedApply?.({});
    setAdvancedOpen(false);
  };
  return (
    <div class="relative h-full w-full">
      <div class="mb-4 flex h-11 items-center justify-between bg-[rgba(255,255,255,.08)] pl-4 pr-2 leading-none">
        <p class="flex">
          {props.label}
          <Show when={props.loading}>
            <span class="ml-2 lds-dual-ring" />
          </Show>
        </p>
        <div class="flex items-center gap-1">
          {props.actions}
          <Show when={props.onAdd}>
            <button
              class="flex h-7 cursor-pointer items-center justify-center bg-[rgba(1,137,102,.25)] px-2 text-sm hover:bg-[rgba(1,137,102,.75)]"
              onClick={() => props.onAdd?.()}
            >
              <DumpIcon name="plus" />
              <p class="pl-2">
                Create{" "}
                {props.label.endsWith("s")
                  ? props.label.slice(0, -1)
                  : props.label}
              </p>
            </button>
          </Show>
          <Show when={props.onSearch}>
            <div class="flex h-7 w-[12.5rem] items-center justify-between bg-[rgba(255,255,255,.11)] pl-2">
              <input
                autocomplete="off"
                type="text"
                placeholder={props.searchPlaceholder ?? "Search"}
                class="h-7 w-full bg-transparent outline-0 focus:border-0 focus-visible:border-0"
                value={props.search ?? ""}
                onInput={(event) => props.onSearch?.(event.currentTarget.value)}
              />
              <div class="px-2">
                <DumpIcon name="input_zoom" />
              </div>
            </div>
          </Show>
          <Show when={props.onAdvancedSearch || props.advancedFields?.length}>
            <button
              class="flex h-7 cursor-pointer items-center justify-center bg-green-gradient px-2 transition-all duration-200 ease-in-out hover:drop-shadow-green-gradient"
              aria-label="Advanced search"
              onClick={openAdvanced}
            >
              <DumpIcon name="nav_config" />
            </button>
          </Show>
        </div>
      </div>

      <Show when={advancedOpen() && props.advancedFields?.length}>
        <div class="modal fixed inset-0 z-[1000] flex items-center justify-center bg-black/65">
          <div class="relative w-full max-w-[42rem] border border-white-11 bg-gray-gradient p-5 shadow-green-gradient">
            <div class="mb-4 flex items-center justify-between">
              <p class="roboto700 text-xl">Advanced search</p>
              <button class="h-8 w-8 bg-red-gradient" onClick={() => setAdvancedOpen(false)}>
                <DumpIcon name="tab_close" />
              </button>
            </div>
            <div class="grid gap-3 md:grid-cols-2">
              <For each={props.advancedFields ?? []}>
                {(field) => (
                  <label class="flex flex-col gap-1 text-sm">
                    <span class="text-white-55">{field.label}</span>
                    <Show when={field.type === "select"} fallback={
                      <input
                        class="min-h-9 bg-white-11 px-3"
                        type={field.type ?? "text"}
                        placeholder={field.placeholder}
                        value={advancedDraft()[field.key] ?? ""}
                        onInput={(event) => setAdvancedDraft((current) => ({ ...current, [field.key]: event.currentTarget.value }))}
                      />
                    }>
                      <select
                        class="min-h-9 bg-white-11 px-3 text-white"
                        value={advancedDraft()[field.key] ?? ""}
                        onChange={(event) => setAdvancedDraft((current) => ({ ...current, [field.key]: event.currentTarget.value }))}
                      >
                        <option value="">Any</option>
                        <For each={field.options ?? []}>
                          {(option) => <option value={option.value}>{option.label}</option>}
                        </For>
                      </select>
                    </Show>
                  </label>
                )}
              </For>
            </div>
            <div class="mt-5 flex justify-end gap-2">
              <button class="bg-white-11 px-4 py-2 text-sm" onClick={resetAdvanced}>Reset</button>
              <button class="bg-green-gradient px-4 py-2 text-sm" onClick={applyAdvanced}>Search</button>
            </div>
          </div>
        </div>
      </Show>

      <div class="h-[calc(100%-4.75rem)] w-full overflow-x-hidden overflow-y-auto pr-1">
        <ul class="list flex flex-col gap-2">
          <For each={props.entries}>
            {(entry) => (
              <li
                class="item-list-bg relative flex h-auto min-h-[5.5rem] cursor-pointer gap-4 bg-gray-gradient p-3"
                onClick={(event) =>
                  props.onSelect?.(entry, event.ctrlKey || event.metaKey)
                }
              >
                <div>
                  <div class="flex h-[3.75rem] w-[3.75rem] items-center justify-center bg-[rgba(255,255,255,.06)]">
                    <Show
                      when={entry.avatar}
                      fallback={<DumpIcon name={entry.icon ?? "nav_reports"} />}
                    >
                      <img src={entry.avatar ?? ""} alt="" />
                    </Show>
                  </div>
                </div>
                <div class="w-[66%]">
                  <div>
                    <p style={{ color: entry.danger ? "#c84d4d" : "#fff" }}>
                      {entry.title}
                      <Show when={entry.titleGrey}>
                        <span class="ml-1 text-xs text-gray-400">
                          {entry.titleGrey}
                        </span>
                      </Show>
                    </p>
                  </div>
                  <p class="break-words pr-6 leading-none text-white-55">
                    {plainText(entry.description ?? "").slice(0, 100)}
                  </p>
                  <ul class="flex pt-2">
                    <Show when={entry.leftLabel}>
                      <li class="flex h-5 bg-cyan/75 px-1 text-xs font-medium leading-5">
                        <p class="text-gray-900">{entry.leftLabel}</p>
                      </li>
                    </Show>
                    <Show when={entry.rightLabel}>
                      <li class="flex h-5 bg-[rgba(255,255,255,.11)] px-2 text-xs leading-5">
                        <p>{entry.rightLabel}</p>
                      </li>
                    </Show>
                  </ul>
                </div>
                <Show when={entry.endLabel}>
                  <div class="min-h-[4rem] w-[33%]">{entry.endLabel}</div>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </div>
    </div>
  );
}

function plainText(value: string) {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed?.ops)) {
      return parsed.ops
        .map((op: { insert?: unknown }) => typeof op.insert === "string" ? op.insert : "")
        .join("")
        .replace(/\s+/g, " ")
        .trim();
    }
  } catch {
    // Legacy incidents may still contain plain text/HTML.
  }
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}
